import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Paper,
    CircularProgress,
    Button,
    TextField,
    Alert
} from '@mui/material';
import { CheckCircle, Error, Email, Send } from '@mui/icons-material';
import AuthService from '../../ui/services/auth-service';
import logo from '../../assets/webp/logo_complete.webp';
import logoWhite from '../../assets/webp/logo_complete_white.webp';
import { useThemeMode } from '../../ui/context/theme/ThemeProvider';

export const ConfirmEmailPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const { mode } = useThemeMode();
    const [status, setStatus] = useState('loading'); // loading | success | error | already_verified
    const [message, setMessage] = useState('');
    const [resendEmail, setResendEmail] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendMessage, setResendMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        const confirmEmail = async () => {
            if (!token) {
                setStatus('error');
                setMessage('Token de confirmación inválido');
                return;
            }

            const result = await AuthService.confirmEmail(token);

            if (result.success) {
                if (result.alreadyVerified) {
                    setStatus('already_verified');
                    setMessage('Tu cuenta ya ha sido verificada previamente');
                } else {
                    setStatus('success');
                    setMessage(result.message || '¡Tu cuenta ha sido activada exitosamente!');
                }
            } else {
                setStatus('error');
                if (result.expired) {
                    setMessage('El token de confirmación ha expirado. Por favor contacta soporte.');
                } else {
                    setMessage(result.error || 'Error al confirmar el email');
                }
            }
        };

        confirmEmail();
    }, [token]);

    const handleResendConfirmation = async () => {
        if (!resendEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resendEmail)) {
            setResendMessage({ type: 'error', text: 'Por favor ingresa un email válido' });
            return;
        }

        setResendLoading(true);
        setResendMessage({ type: '', text: '' });

        const result = await AuthService.resendConfirmationEmail(resendEmail);

        setResendLoading(false);

        if (result.success) {
            setResendMessage({ 
                type: 'success', 
                text: result.message || 'Email de confirmación enviado exitosamente. Por favor revisa tu bandeja de entrada.' 
            });
            setResendEmail('');
        } else {
            if (result.alreadyVerified) {
                setResendMessage({ 
                    type: 'info', 
                    text: 'Esta cuenta ya está verificada. Puedes iniciar sesión directamente.' 
                });
            } else {
                setResendMessage({ 
                    type: 'error', 
                    text: result.error || 'Error al reenviar email de confirmación' 
                });
            }
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'loading':
                return (
                    <>
                        <Email sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                        <Typography variant="h5" gutterBottom>
                            Confirmando tu email...
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Por favor espera un momento
                        </Typography>
                    </>
                );

            case 'success':
                return (
                    <>
                        <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            ¡Email Confirmado!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 1, mb: 3 }}>
                            {message}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                            Ahora puedes iniciar sesión en tu cuenta de SecureSign
                        </Typography>
                        <Button
                            component={Link}
                            to="/login"
                            variant="contained"
                            size="large"
                            sx={{ mt: 2 }}
                        >
                            Ir a Iniciar Sesión
                        </Button>
                    </>
                );

            case 'already_verified':
                return (
                    <>
                        <CheckCircle sx={{ fontSize: 60, color: 'info.main', mb: 2 }} />
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            Cuenta Ya Verificada
                        </Typography>
                        <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 1, mb: 3 }}>
                            {message}
                        </Typography>
                        <Button
                            component={Link}
                            to="/login"
                            variant="contained"
                            size="large"
                            sx={{ mt: 2 }}
                        >
                            Ir a Iniciar Sesión
                        </Button>
                    </>
                );

            case 'error':
                return (
                    <>
                        <Error sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            Error al Confirmar Email
                        </Typography>
                        <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 1, mb: 3 }}>
                            {message}
                        </Typography>

                        {/* Formulario para reenviar confirmación */}
                        <Box sx={{ width: '100%', maxWidth: 400, mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                                ¿Necesitas un nuevo email de confirmación?
                            </Typography>
                            
                            {resendMessage.text && (
                                <Alert severity={resendMessage.type} sx={{ mb: 2 }}>
                                    {resendMessage.text}
                                </Alert>
                            )}

                            <TextField
                                fullWidth
                                label="Tu correo electrónico"
                                type="email"
                                value={resendEmail}
                                onChange={(e) => setResendEmail(e.target.value)}
                                disabled={resendLoading}
                                sx={{ mb: 2 }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleResendConfirmation();
                                    }
                                }}
                            />
                            
                            <Button
                                fullWidth
                                variant="outlined"
                                size="large"
                                startIcon={resendLoading ? <CircularProgress size={20} /> : <Send />}
                                onClick={handleResendConfirmation}
                                disabled={resendLoading}
                                sx={{ mb: 2 }}
                            >
                                {resendLoading ? 'Enviando...' : 'Reenviar Email de Confirmación'}
                            </Button>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', mt: 2 }}>
                            <Button
                                component={Link}
                                to="/login"
                                variant="contained"
                                size="large"
                            >
                                Ir a Iniciar Sesión
                            </Button>
                            <Button
                                component={Link}
                                to="/register"
                                variant="outlined"
                                size="large"
                            >
                                Crear Nueva Cuenta
                            </Button>
                        </Box>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Container component="main" maxWidth="sm">
                <Paper
                    elevation={6}
                    sx={{
                        p: { xs: 5, sm: 6 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        maxWidth: '500px',
                        mx: 'auto',
                        borderRadius: 2
                    }}
                >
                    <Box
                        component="img"
                        src={mode === 'dark' ? logoWhite : logo}
                        alt="Neftis SecureSign"
                        sx={{ 
                            width: '60%',
                            maxWidth: '220px',
                            mb: 4,
                            mt: 0
                        }}
                    />
                    {renderContent()}
                </Paper>
            </Container>
        </Box>
    );
};

export default ConfirmEmailPage;
